import React from 'react';
import { VoteData } from '../types';

interface RatingButtonsProps {
  plateId: string;
  votes: VoteData;
  onVote: (plateId: string, voteType: 'upvote' | 'downvote') => void;
  hasVoted: boolean;
  compact?: boolean;
}

const RatingButtons: React.FC<RatingButtonsProps> = ({ 
  plateId, 
  votes, 
  onVote, 
  hasVoted,
  compact = false
}) => {
  const handleVote = (voteType: 'upvote' | 'downvote') => {
    if (!hasVoted) {
      onVote(plateId, voteType);
    }
  };

  const getScoreColor = (score: number) => {
    if (score > 0) return 'text-green-600 dark:text-green-400';
    if (score < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-slate-400';
  };

  if (compact) {
    return (
      <div className="flex items-center gap-1" data-rating-button>
        <button
          onClick={() => handleVote('upvote')}
          disabled={hasVoted}
          className={`p-1.5 rounded-full transition-colors ${
            hasVoted
              ? 'text-gray-300 dark:text-slate-600 cursor-not-allowed'
              : 'text-gray-600 dark:text-slate-400 hover:text-green-600 dark:hover:text-green-400'
          }`}
          title={hasVoted ? 'Already voted' : 'Upvote'}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-7" />
          </svg>
        </button>
        <span className={`text-sm font-semibold min-w-[2rem] ${getScoreColor(votes.total)}`}>
          {votes.total > 0 ? '+' : ''}{votes.total}
        </span>
        <button
          onClick={() => handleVote('downvote')}
          disabled={hasVoted}
          className={`p-1.5 rounded-full transition-colors ${
            hasVoted
              ? 'text-gray-300 dark:text-slate-600 cursor-not-allowed'
              : 'text-gray-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400'
          }`}
          title={hasVoted ? 'Already voted' : 'Downvote'}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-4h2" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-4" data-rating-button>
      <div className={`text-lg font-bold ${getScoreColor(votes.total)}`}>
        {votes.total > 0 ? '+' : ''}{votes.total}
      </div>

      <div className="flex items-center space-x-2">
        <button
          onClick={() => handleVote('upvote')}
          disabled={hasVoted}
          className={`p-2 rounded-full transition-colors ${
            hasVoted
              ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
              : 'bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-500/30'
          }`}
          title={hasVoted ? 'Already voted' : 'Upvote this plate'}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>

        <button
          onClick={() => handleVote('downvote')}
          disabled={hasVoted}
          className={`p-2 rounded-full transition-colors ${
            hasVoted
              ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
              : 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-500/30'
          }`}
          title={hasVoted ? 'Already voted' : 'Downvote this plate'}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      <div className="text-sm text-gray-500 dark:text-slate-400">
        <span className="text-green-600 dark:text-green-400">{votes.upvotes}</span> / <span className="text-red-600 dark:text-red-400">{votes.downvotes}</span>
      </div>
    </div>
  );
};

export default RatingButtons;
